import { Link } from 'react-router-dom'
import { ArrowRight, Calendar } from 'lucide-react'
import { fmtDateLong } from './intakeUi'

/**
 * IntakeChooser — the shared "Choose your intake" section.
 *
 * Renders every intake the server reports as open to applicants, in the order
 * it promoted them. Editorial rows with dividers and typography rather than a
 * card grid: the intakes are two doors into the same academy, not two products
 * being compared.
 *
 * Every visible value — name, dates, descriptor, apply destination — comes from
 * the public intake payload. Nothing about a specific intake is written here,
 * so an intake that opens or closes changes this section with no code change.
 *
 * Props:
 *   intakes   — open intake records from useActiveIntake().openIntakes
 *   enquiryHref — where "Talk to Admissions" goes
 */
export default function IntakeChooser({ intakes = [], enquiryHref = '/advisor' }) {
  if (!intakes.length) return null
  const single = intakes.length === 1

  return (
    <section aria-labelledby="choose-intake-heading" className="py-10 sm:py-16">
      <div className="border-b border-white/10 pb-4 mb-2">
        <span className="cinema-label">Admissions</span>
        <h2
          id="choose-intake-heading"
          className="font-display text-xl sm:text-2xl md:text-3xl text-white font-light tracking-tight mt-3"
        >
          {single ? 'The intake now open' : 'Choose your intake'}
        </h2>
        <p className="text-xs sm:text-sm text-cinema-muted font-sans mt-2 max-w-2xl">
          {single
            ? 'Applications are open for the intake below. Your place is confirmed once the registration fee is paid.'
            : 'More than one intake is accepting applications. Pick the one that fits your schedule — you apply directly into it.'}
        </p>
      </div>

      <ul className="divide-y divide-white/10">
        {intakes.map((intake) => (
          <li key={intake.id}>
            <article className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 py-7 sm:py-9 items-start">
              {/* Identity */}
              <div className="lg:col-span-5">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-display text-lg sm:text-xl md:text-2xl text-white tracking-tight">
                    {intake.displayName || intake.name}
                  </h3>
                  {intake.status === 'closing-soon' && (
                    <span className="text-[0.65rem] uppercase tracking-[0.2em] text-cinema-gold font-sans font-semibold">
                      Closing soon
                    </span>
                  )}
                </div>
                {intake.programLabel && (
                  <p className="text-[0.7rem] uppercase tracking-[0.2em] text-cinema-muted font-sans mt-2">
                    {intake.programLabel}
                  </p>
                )}
              </div>

              {/* Dates + descriptor */}
              <div className="lg:col-span-4">
                {intake.startDate && (
                  <p className="flex items-center gap-2 text-xs sm:text-sm text-cinema-soft font-sans">
                    <Calendar size={14} className="text-cinema-gold shrink-0" />
                    Starts {fmtDateLong(intake.startDate)}
                  </p>
                )}
                {intake.registrationEnd && (
                  <p className="text-xs text-cinema-muted font-sans mt-1.5">
                    Applications close {fmtDateLong(intake.registrationEnd)}
                  </p>
                )}
                {intake.programSummary && (
                  <p className="text-xs sm:text-sm text-cinema-muted/90 font-sans mt-3 leading-relaxed">
                    {intake.programSummary}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="lg:col-span-3 flex flex-col items-stretch lg:items-end gap-2.5">
                <Link
                  to={intake.applyHref}
                  className="cinema-btn-primary !px-5 !py-3 !text-xs uppercase tracking-wider font-bold inline-flex items-center justify-center gap-2 min-h-[44px] w-full lg:w-auto"
                >
                  <span>Apply for {intake.shortLabel || intake.displayName}</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to={intake.programmesHref || '/programs'}
                  className="cinema-btn-text text-xs uppercase tracking-wider font-semibold inline-flex items-center justify-center lg:justify-end gap-1.5 min-h-[32px]"
                >
                  View programmes
                </Link>
              </div>
            </article>
          </li>
        ))}
      </ul>

      <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <p className="text-xs text-cinema-muted font-sans flex-1">
          Not sure which intake suits you? Admissions can talk it through with you.
        </p>
        <Link
          to={enquiryHref}
          className="cinema-btn-ghost !px-5 !py-3 !text-xs uppercase tracking-wider font-semibold inline-flex items-center justify-center gap-2 border border-white/20 hover:border-cinema-gold/60 min-h-[44px]"
        >
          Talk to Admissions
        </Link>
      </div>
    </section>
  )
}
